import { useState, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import { globalSignOutUtil } from '../utils/globalSignOut';

/**
 * Custom hook for managing authentication state and operations
 * @param {Object} options - Configuration options
 * @param {Function} options.createSnack - Function to show snackbar messages
 * @returns {Object} - Authentication state and methods
 */
export const useAuth = ({ createSnack }) => {
  const [isSigningOut, setIsSigningOut] = useState(false);

  /**
   * Perform global sign out operation
   * @param {string} accessToken - User's access token
   * @param {string} region - AWS region
   * @returns {Promise<Object>} - Result of the global sign out
   */
  const performGlobalSignOut = useCallback(async (accessToken, region) => {
    if (!globalSignOutUtil.isGlobalSignOutSupported({ accessToken })) {
      return {
        success: false,
        error: 'Global sign out not supported for current user',
        code: 'UnsupportedOperation'
      };
    }

    return await globalSignOutUtil.performGlobalSignOut(accessToken, region);
  }, []);

  /**
   * Broadcast a cross-tab sign out event using BroadcastChannel and localStorage
   */
  const broadcastCrossTabSignOut = useCallback(() => {
    try {
      if (typeof window !== 'undefined') {
        if ('BroadcastChannel' in window) {
          const bc = new BroadcastChannel('apix-auth');
          bc.postMessage({ type: 'SIGN_OUT', ts: Date.now() });
          bc.close();
        }
        if (window.localStorage) {
          window.localStorage.setItem('apix:signout', String(Date.now()));
        }
      }
    } catch (_) {}
  }, []);

  /**
   * Sign out user with global sign out support
   * @param {Object} user - User object with access token
   * @param {string} region - AWS region
   * @param {boolean} enableGlobalSignOut - Whether to perform global sign out
   * @returns {Promise<Object>} - Result of the sign out operation
   */
  const signOut = useCallback(async (user, region, enableGlobalSignOut = true) => {
    setIsSigningOut(true);
    
    try {
      const results = {
        localSignOut: { success: false },
        globalSignOut: { success: false }
      };

      // Perform global sign out if enabled.
      // Fetch a fresh Cognito access token from Amplify rather than reading
      // user.accessToken, which holds the ID token (used for API Gateway calls).
      // GlobalSignOutCommand requires the access token specifically.
      if (enableGlobalSignOut && region) {
        let cognitoAccessToken;
        try {
          const session = await Auth.currentSession();
          cognitoAccessToken = session.accessToken.jwtToken;
        } catch (_) {}

        if (cognitoAccessToken) {
          const globalResult = await performGlobalSignOut(cognitoAccessToken, region);
          results.globalSignOut = globalResult;

          if (!globalResult.success) {
            console.warn('Global sign out failed:', globalResult.error);
          }
        }
      }

      // Perform local sign out
      try {
        await Auth.signOut();
        results.localSignOut.success = true;
        // Broadcast cross-tab sign out so other tabs immediately sign out
        broadcastCrossTabSignOut();
      } catch (error) {
        console.error('Local sign out failed:', error);
        results.localSignOut.error = error.message;
      }

      // Show appropriate message to user
      if (results.globalSignOut.success) {
        createSnack?.('Signed out from all devices successfully', 'success', 3000);
      } else if (results.localSignOut.success) {
        createSnack?.('Signed out successfully', 'success', 3000);
      } else {
        createSnack?.('Sign out completed with some issues', 'warning', 3000);
      }

      return {
        success: results.localSignOut.success,
        results
      };

    } catch (error) {
      console.error('Sign out error:', error);
      createSnack?.('Sign out failed', 'error', 3000);
      
      return {
        success: false,
        error: error.message
      };
    } finally {
      setIsSigningOut(false);
    }
  }, [performGlobalSignOut, createSnack, broadcastCrossTabSignOut]);

  return {
    isSigningOut,
    signOut,
    performGlobalSignOut
  };
}; 